import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CloudDownloadOutlined,
  FileDownloadOutlined,
  GroupsOutlined,
  LinkOffOutlined,
  OpenInNew,
  SendOutlined,
  TableRowsOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import { MyBusinessSchema, ResourceSchema } from '../data/types';
import {
  createContactBrief,
  disconnectContactProject,
  handoffContactList,
  importContactHub,
  listContactRecords,
  loadContactSource,
  loadContactSourceWithCredentials,
  startContactRun,
} from '../api/crud';
import { useAllBusinesses } from '../hooks/businesshooks';
import { briefFromBusiness, CONTACT_CONSENT_VERSION, dscvryOpenUrl, NOT_SHARED, platformHandoffUrl } from '../utils/contactBuilder';
import { DscvryBusinessSelect } from './DscvryBusinessSelect';
import ContactSheetModal, { SheetRow, StatusChip, downloadContactsCsv } from './ContactSheetModal';

type ContactListBuilderProps = {
  resource: ResourceSchema
  uid?: string
  operatorEmail?: string
  onSignIn?: () => void
};

type ContactRow = SheetRow & { sample_directory?: boolean };
type BriefResponse = { project?: { id?: string }; brief?: { id?: string } };
type HubAnswers = {
  source_seeds?: unknown[]
  qualifying_signals?: string[]
  research_context?: { target_customers?: unknown[] }
};
type ExistingLists = {
  homecook: { project_id: string; brief_id?: string; business_id: string; connection_status?: string; record_count: number }[]
  hub_contact_count: number
  hub_page_count?: number
  hub_contacts_url: string
  hub_loaded: boolean
  hub_imported?: boolean
};
type RunResearch = { search_configured?: boolean; source_pages?: number; stored_records?: number };
type RunCounts = { invalid?: number; uncertain?: number; active_total?: number };
type SourceResponse = {
  imported_business_id?: string
  existing_lists?: ExistingLists
  venture?: { target_customers?: { label?: string }[] }
  answers?: HubAnswers
  setup?: {
    sample?: boolean
    imported?: boolean
    steps_found?: string[]
    venture_name?: string
    concept?: string
    target_customers?: string[]
    qualifying_signals?: string[]
    source_seeds?: unknown[]
  }
};

/**
 * Generates a contact list for one selected pantry business.
 * Target customers and research prompts stay in the DSCVRY Business Hub.
 */
const ContactListBuilder = ({ resource, uid, operatorEmail, onSignIn }: ContactListBuilderProps) => {
  const [params, setParams] = useSearchParams();
  const requestedBusiness = params.get('business') || '';
  const { businesses, isLoadingAllBusinesses } = useAllBusinesses(uid || '');
  const rows = (businesses || []) as MyBusinessSchema[];
  const [businessId, setBusinessId] = useState(requestedBusiness);
  const [step, setStep] = useState<'confirm' | 'revoked'>('confirm');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<BriefResponse | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [openUrl, setOpenUrl] = useState('');
  const [existing, setExisting] = useState<ExistingLists | null>(null);
  const [hubAnswers, setHubAnswers] = useState<HubAnswers | null>(null);
  const [hubSegments, setHubSegments] = useState<string[]>([]);
  const [runResearch, setRunResearch] = useState<RunResearch | null>(null);
  const [runCounts, setRunCounts] = useState<RunCounts | null>(null);
  const [stepsFound, setStepsFound] = useState<string[]>([]);
  const [importName, setImportName] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [handed, setHanded] = useState(false);
  const [showLoadForm, setShowLoadForm] = useState(false);
  const [airtableUrl, setAirtableUrl] = useState('');
  const [airtableApiKey, setAirtableApiKey] = useState('');

  useEffect(() => {
    if (requestedBusiness) setBusinessId(requestedBusiness);
  }, [requestedBusiness]);

  const selected = useMemo(() => rows.find((item) => item.id === businessId), [rows, businessId]);
  const hubUrl = dscvryOpenUrl(resource);
  const storedProject = useMemo(() => {
    const lists = existing?.homecook || [];
    return lists.find((item) => item.business_id === businessId && item.record_count > 0)
      || lists.find((item) => item.record_count > 0);
  }, [existing, businessId]);

  useEffect(() => {
    if (!uid || !resource.id) return;
    let cancelled = false;
    loadContactSource(uid, resource.id, businessId).then((data: SourceResponse | null) => {
      if (cancelled || !data?.existing_lists) return;
      applySource(data);
      if (!businessId && data.imported_business_id) chooseBusiness(data.imported_business_id);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [uid, resource.id, businessId]);

  useEffect(() => {
    if (!uid || !resource.id || !storedProject?.project_id) return;
    let cancelled = false;
    listContactRecords(uid, resource.id, storedProject.project_id).then((listed: ContactRow[]) => {
      if (cancelled) return;
      const rows = listed || [];
      setContacts(rows);
      setSaved({ project: { id: storedProject.project_id }, brief: storedProject.brief_id ? { id: storedProject.brief_id } : undefined });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [uid, resource.id, storedProject?.project_id, storedProject?.brief_id, existing?.hub_contact_count]);

  const chooseBusiness = (id: string) => {
    setBusinessId(id);
    const next = new URLSearchParams(params);
    if (id) next.set('business', id);
    else next.delete('business');
    setParams(next, { replace: true });
  };

  const applySource = (data: SourceResponse | null) => {
    const lists = data?.existing_lists || null;
    const setup = data?.setup;
    setExisting(lists);
    if (lists?.hub_loaded) {
      setHubSegments((setup?.target_customers || []).slice(0, 2));
      setStepsFound(setup?.steps_found || []);
      setHubAnswers({
        source_seeds: setup?.source_seeds || data?.answers?.source_seeds || [],
        qualifying_signals: setup?.qualifying_signals || data?.answers?.qualifying_signals || [],
      });
    } else {
      setHubSegments([]);
      setStepsFound([]);
      setHubAnswers(null);
    }
  };

  /** Toggle the secondary Load form. Credentials are collected only when Load is used. */
  const openLoadForm = () => {
    if (!uid || !resource.id) {
      onSignIn?.();
      return;
    }
    if (!businessId) {
      setError('Select a business before loading its DSCVRY venture.');
      return;
    }
    setError('');
    setShowLoadForm((open) => !open);
  };

  /** Load venture with the member's Airtable hub URL and API key (request-scoped). */
  const loadHub = async () => {
    if (!uid || !resource.id) {
      onSignIn?.();
      return;
    }
    if (!businessId) {
      setError('Select a business before loading its DSCVRY venture.');
      return;
    }
    const hub = airtableUrl.trim();
    const key = airtableApiKey.trim();
    if (!hub || !key) {
      setError('Enter your Airtable hub URL and API key to load from DSCVRY.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      applySource(await loadContactSourceWithCredentials(uid, resource.id, businessId, hub, key) as SourceResponse | null);
      setAirtableApiKey('');
      setShowLoadForm(false);
    } catch (err) {
      setError('The DSCVRY Business Hub could not be loaded. Check the Airtable hub URL and API key.');
    } finally {
      setBusy(false);
    }
  };

  const importSteps = async (file: File | undefined) => {
    if (!file) return;
    if (!uid || !resource.id) {
      onSignIn?.();
      return;
    }
    if (!businessId) {
      setError('Select a business before importing its DSCVRY steps.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const markdown = await file.text();
      const data = await importContactHub(uid, resource.id, businessId, markdown, file.name) as SourceResponse | null;
      setImportName(file.name);
      applySource(data);
    } catch (err) {
      setError('The DSCVRY steps file could not be imported. Use the six step headings and at least one target customer.');
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!uid || !resource.id) {
      onSignIn?.();
      return;
    }
    if (!selected || !consent) return;
    const briefInput = briefFromBusiness(selected);
    if (hubAnswers?.source_seeds && !existing?.hub_imported) {
      const answers = briefInput.answers as { source_seeds?: unknown[]; qualifying_signals?: string[] };
      answers.source_seeds = (hubAnswers.source_seeds as { list_id?: string }[]).map((seed) => ({ ...seed, list_id: 'primary' }));
      const signals = new Set([...(answers.qualifying_signals || []), ...(hubAnswers.qualifying_signals || [])]);
      answers.qualifying_signals = Array.from(signals);
    }
    setBusy(true);
    setError('');
    try {
      const brief = await createContactBrief(uid, resource.id, {
        business_id: briefInput.business_id,
        business_description: briefInput.business_description,
        answers: briefInput.answers,
        consent: {
          granted: true,
          granted_at: new Date().toISOString(),
          notice_version: CONTACT_CONSENT_VERSION,
        },
      });
      setSaved(brief);
      const run = brief?.brief?.id ? await startContactRun(uid, resource.id, brief.brief.id) : null;
      setRunResearch(run?.research || null);
      setRunCounts(run?.counts || null);
      const listed = brief?.project?.id ? await listContactRecords(uid, resource.id, brief.project.id) : [];
      setContacts(listed || []);
    } catch (err) {
      setError('The contact list could not be generated. Nothing was handed to DSCVRY.');
    } finally {
      setBusy(false);
    }
  };

  const handoff = async () => {
    if (!uid || !resource.id || !saved?.brief?.id) {
      setError('Generate the list once before handing it to DSCVRY.');
      return;
    }
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    setError('');
    try {
      const packageResult = await handoffContactList(uid, resource.id, saved.brief.id);
      const target = platformHandoffUrl(resource, packageResult?.open_url || '', saved.project?.id || '');
      setOpenUrl(target);
      setHanded(true);
      if (tab) {
        tab.opener = null;
        tab.location.href = target;
      } else {
        window.open(target, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      tab?.close();
      setError('The list is not ready to hand off.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!uid || !resource.id || !saved?.project?.id) return;
    setBusy(true);
    try {
      await disconnectContactProject(uid, resource.id, saved.project.id);
      setStep('revoked');
    } catch (err) {
      setError('Disconnect could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const stepsChip = hubSegments.length > 0
    ? `${existing?.hub_imported ? `${stepsFound.length} of 6 steps imported` : 'Loaded from DSCVRY'} · ${hubSegments.length} target ${hubSegments.length === 1 ? 'customer' : 'customers'}`
    : '';

  return (
    <section id="contact-list-builder" className="mt-4 rounded-2xl border border-[#E4EEF1] bg-white p-5 font-open-sans text-sm" data-testid="contact-list-builder">
      <p className="page-kicker">Contact research</p>
      <h2 className="mt-1 font-raleway text-lg font-semibold text-ink">Build a verified contact list</h2>
      <p className="mt-1 text-[#5B7480]">
        For one selected pantry business. Generating a list does not approve a facility or contact anyone.
      </p>

      {step === 'confirm' && (
        <div className="mt-5 space-y-5">
          <SectionBlock title="Business">
            <DscvryBusinessSelect uid={uid} businessId={businessId} onChange={chooseBusiness} hubUrl={hubUrl} />
            <div className="flex flex-wrap items-center gap-2">
              <label className={`${PRIMARY_BUTTON} cursor-pointer`} title="One .md file with the six DSCVRY step headings">
                <UploadFileOutlined sx={{ fontSize: 16 }} />
                Import steps (.md)
                <input
                  type="file"
                  accept=".md,.markdown,text/markdown,text/plain"
                  className="sr-only"
                  disabled={busy}
                  data-testid="dscvry-steps-file"
                  onChange={(event) => { importSteps(event.target.files?.[0]); event.target.value = ''; }}
                />
              </label>
              <button type="button" className={QUIET_BUTTON} onClick={openLoadForm} disabled={busy} data-testid="dscvry-load-toggle">
                <CloudDownloadOutlined sx={{ fontSize: 16 }} />
                Load venture from DSCVRY
              </button>
              {stepsChip && (
                <span className="inline-flex items-center rounded-full bg-[#F1ECFB] px-2.5 py-1 text-xs font-semibold text-[#3B2A63]" data-testid="dscvry-steps-chip" title={importName || undefined}>
                  {stepsChip}
                </span>
              )}
            </div>
            {showLoadForm && (
              <div className="space-y-3 rounded-lg border border-[#E4EEF1] bg-[#F6FAFB] px-3 py-3" data-testid="dscvry-load-form">
                <p className="text-xs text-[#5B7480]">
                  Use your DSCVRY Business Hub Airtable URL and personal API key. These are not taken from community partner setup and are not stored after this load.
                </p>
                <label className="block space-y-1">
                  <span className="font-raleway text-xs font-semibold text-[#5B7480]">Airtable hub URL</span>
                  <input
                    type="url"
                    className="w-full rounded-lg border border-[#E4EEF1] bg-white px-3 py-2 text-ink"
                    placeholder="https://airtable.com/app…/shr…/tbl…/viw…"
                    value={airtableUrl}
                    onChange={(event) => setAirtableUrl(event.target.value)}
                    data-testid="dscvry-airtable-url"
                    autoComplete="off"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="font-raleway text-xs font-semibold text-[#5B7480]">Airtable API key</span>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-[#E4EEF1] bg-white px-3 py-2 text-ink"
                    placeholder="pat…"
                    value={airtableApiKey}
                    onChange={(event) => setAirtableApiKey(event.target.value)}
                    data-testid="dscvry-airtable-key"
                    autoComplete="off"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={PRIMARY_BUTTON} onClick={loadHub} disabled={busy} data-testid="dscvry-load-submit">
                    <CloudDownloadOutlined sx={{ fontSize: 16 }} />
                    {busy ? 'Loading' : 'Load venture'}
                  </button>
                  <button
                    type="button"
                    className={QUIET_BUTTON}
                    disabled={busy}
                    onClick={() => { setShowLoadForm(false); setAirtableApiKey(''); setError(''); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {hubSegments.length > 0 && (
              <div className="rounded-lg bg-[#F7F4FC] px-3 py-2" data-testid="hub-segments">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6B5A8A]">Target customers</p>
                <div className="flex flex-wrap gap-1.5">
                  {hubSegments.map((label) => (
                    <span key={label} className={HUB_SEGMENT} data-testid="hub-segment">
                      <GroupsOutlined sx={{ fontSize: 14 }} />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionBlock>

          <StoredContacts
            businessName={selected?.business_name}
            contacts={contacts}
            runCounts={runCounts}
            runResearch={runResearch}
            existing={existing}
            busy={busy}
            handed={handed}
            openUrl={openUrl}
            onHandoff={handoff}
            onOpenSheet={() => setSheetOpen(true)}
            onDisconnect={disconnect}
          />
          <ContactSheetModal
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            rows={visibleRows(contacts, existing)}
            businessName={selected?.business_name}
            airtableUrl={existing?.hub_contacts_url}
          />

          <SectionBlock title="Generate">
            {uid && isLoadingAllBusinesses && <p className="text-[#5B7480]">Loading your businesses.</p>}
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={consent} onChange={(event) => setConsent(event.target.checked)} data-testid="contact-consent" />
              <span>I authorize HomeCook to use the selected business profile to generate this contact list.</span>
            </label>
            <details className="rounded-lg bg-[#F6FAFB] px-3 py-2 text-[#3F5B64]" data-testid="what-is-shared">
              <summary className="cursor-pointer font-raleway font-semibold text-ink">What is shared</summary>
              <div className="mt-2 space-y-1">
                <p><span className="font-semibold">Shared: </span>the selected business name, description, category, and city.</p>
                <p><span className="font-semibold">Not shared: </span>{NOT_SHARED.join(', ')}.</p>
                <p><span className="font-semibold">Operator email: </span>{operatorEmail || '—'}. This is the operator contact, not a researched lead.</p>
              </div>
            </details>
            {error && <p className="text-[#B91C1C]">{error}</p>}
            <button
              type="button"
              disabled={!selected || !consent || busy}
              className={PRIMARY_BUTTON}
              onClick={generate}
            >
              <GroupsOutlined sx={{ fontSize: 16 }} />
              {busy ? 'Generating' : 'Generate contact list'}
            </button>
          </SectionBlock>
        </div>
      )}

      {step === 'revoked' && (
        <p className="mt-3">
          Future synchronization has stopped. Existing research records stay until a separate deletion request. This does not delete historical records.
        </p>
      )}
    </section>
  );
};

const PRIMARY_BUTTON = 'inline-flex items-center gap-1.5 rounded-lg bg-primaryDark-0 px-3 py-2 font-raleway text-white disabled:opacity-50';
const QUIET_BUTTON = 'inline-flex items-center gap-1.5 rounded-lg border border-[#E4EEF1] bg-white px-3 py-2 font-raleway text-ink hover:bg-[#F6FAFB] disabled:opacity-50';
const HUB_SEGMENT = 'inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs font-semibold text-[#3B2A63] shadow-[inset_0_0_0_1px_#E4D8F5]';

const SectionBlock = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="space-y-3 border-t border-[#EEF2F4] pt-4 first:border-t-0 first:pt-0">
    <p className="font-raleway text-xs font-semibold uppercase tracking-wide text-[#5B7480]">{title}</p>
    {children}
  </div>
);

/** Rows that belong to the selected business. Sample-directory pages drop out once the six steps are imported. */
const visibleRows = (contacts: ContactRow[], existing: ExistingLists | null) => (
  existing?.hub_imported ? contacts.filter((row) => !row.sample_directory) : contacts
);

const hostOf = (url?: string) => {
  try { return new URL(url || '').host.replace(/^www\./, ''); } catch { return ''; }
};

const StoredContacts = ({
  businessName,
  contacts,
  runCounts,
  runResearch,
  existing,
  busy,
  handed,
  openUrl,
  onHandoff,
  onOpenSheet,
  onDisconnect,
}: {
  businessName?: string
  contacts: ContactRow[]
  runCounts: RunCounts | null
  runResearch: RunResearch | null
  existing: ExistingLists | null
  busy: boolean
  handed: boolean
  openUrl: string
  onHandoff: () => void
  onOpenSheet: () => void
  onDisconnect: () => void
}) => {
  if (!existing && contacts.length === 0) return null;
  const rows = visibleRows(contacts, existing);
  const imported = Boolean(existing?.hub_imported);
  const activeCount = rows.filter((row) => row.record_status === 'active').length;
  const reviewCount = rows.filter((row) => row.record_status === 'uncertain').length;
  const missingRoute = rows.length > 0 && rows.every((row) => !row.public_business_email && !row.public_business_phone);
  const searchOff = Boolean(runResearch && !runResearch.search_configured);
  const hubCount = existing?.hub_contact_count || 0;
  const revoked = Boolean(existing?.homecook.some((item) => item.connection_status === 'revoked' && item.record_count > 0));
  const notice = [
    reviewCount === 1 ? '1 row needs review before outreach.' : `${reviewCount} rows need review before outreach.`,
    missingRoute ? 'No public email or phone was found.' : '',
    searchOff ? 'Web search is not connected.' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className="space-y-3 border-t border-[#EEF2F4] pt-4" data-testid="stored-contacts">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-raleway text-xs font-semibold uppercase tracking-wide text-[#5B7480]">Contact list</p>
          <p className="mt-1 font-raleway font-semibold text-ink">Contact list for {businessName || 'this business'}</p>
          <p className="text-xs text-[#5B7480]">
            Stored in HomeCook{imported ? ' · DSCVRY steps imported' : ''}{revoked ? ' · future sync stopped' : ''}
          </p>
        </div>
        <div className="flex gap-1.5 text-xs">
          <CountChip label="total" value={rows.length} tone="bg-[#EEF2F4] text-ink" />
          <CountChip label="active" value={activeCount} tone="bg-[#E7F6EC] text-[#1E7B3C]" />
          <CountChip label="needs review" value={reviewCount} tone="bg-[#FFF4DB] text-[#8A5A00]" />
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-[#5B7480]">
          No contacts stored for this business yet.{searchOff ? ' Web search is not connected.' : ''} Nothing was invented or handed to DSCVRY.
        </p>
      ) : (
        <>
          {activeCount === 0 && <p className="text-xs text-[#8A5A00]">{notice} Nothing was invented.</p>}
          {(runCounts?.invalid || 0) > 0 && <p className="text-xs text-[#5B7480]">{runCounts?.invalid} research pages could not be read.</p>}
          <ul className="divide-y divide-[#EEF2F4] rounded-lg border border-[#E4EEF1]">
            {rows.slice(0, 5).map((row) => {
              const route = [row.role, row.public_business_email, row.public_business_phone].filter(Boolean).join(' · ');
              return (
                <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{row.contact_name || row.organization || 'Business record'}</p>
                    <p className="truncate text-xs text-[#5B7480]">
                      {route && <span>{route} · </span>}
                      {row.source_url
                        ? <a href={row.source_url} target="_blank" rel="noreferrer" className="hover:underline">{hostOf(row.source_url)}</a>
                        : 'No source page'}
                    </p>
                  </div>
                  <StatusChip status={row.record_status} />
                </li>
              );
            })}
          </ul>
          {rows.length > 5 && <p className="text-xs text-[#5B7480]">{rows.length - 5} more in the contacts view.</p>}
        </>
      )}

      {hubCount > 0 && <p className="text-xs text-[#5B7480]">{hubCount} contacts already live in the DSCVRY Business Hub.</p>}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={QUIET_BUTTON} onClick={onOpenSheet} data-testid="open-contacts-view">
          <TableRowsOutlined sx={{ fontSize: 16 }} />
          Open the contacts view
        </button>
        <button type="button" className={QUIET_BUTTON} disabled={rows.length === 0} onClick={() => downloadContactsCsv(rows, businessName)}>
          <FileDownloadOutlined sx={{ fontSize: 16 }} />
          Download CSV
        </button>
        <button type="button" className={`${PRIMARY_BUTTON} sm:ml-auto`} onClick={onHandoff} disabled={busy || rows.length === 0} data-testid="hand-to-dscvry">
          <SendOutlined sx={{ fontSize: 16 }} />
          {busy ? 'Handing off' : 'Hand list to DSCVRY'}
        </button>
      </div>

      {handed && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#3B2A63]" data-testid="handed-banner">
          <span className="font-semibold">Handed to DSCVRY</span>
          {openUrl && (
            <a href={openUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold underline">
              <OpenInNew sx={{ fontSize: 14 }} />
              Open DSCVRY
            </a>
          )}
          <button type="button" className="inline-flex items-center gap-1 underline" onClick={onDisconnect} disabled={busy}>
            <LinkOffOutlined sx={{ fontSize: 14 }} />
            Disconnect
          </button>
          <span className="text-[#5B7480]">Preview until someone approves the import. Outreach fields stay blank.</span>
        </p>
      )}
    </div>
  );
};

const CountChip = ({ label, value, tone }: { label: string; value: number; tone: string }) => (
  <span className={`rounded-full px-2 py-1 font-semibold ${tone}`}>{value} {label}</span>
);

export default ContactListBuilder;
